/**
 * Exportação do CRM — funções puras (sem jsPDF/xlsx, para testar direto).
 *
 * A linha vem de GET /api/crm/leads/exportar (LeadExportSerializer, CRM-35):
 * o lead inteiro, uma linha por lead, com endereço principal achatado e
 * parceiros. O board não serve mais de fonte — ele é leve de propósito
 * (padrão Lista × Detalhe) e não tem empresa, endereço, telefone 2, bio,
 * nascimento nem parceiros.
 */

/** Uma linha exportável a partir de um item de `exportarLeads`. */
export function linhaDeExportacao(l) {
  const parceiros = Array.isArray(l.parceiros) ? l.parceiros : [];
  return {
    numero: l.numero || "",
    titulo: l.titulo || "",
    etapa: l.etapa_nome || "",
    grupo: l.grupo_nome || "",
    status: l.status_display || l.status || "",
    temperatura: l.temperatura_display || l.temperatura || "",
    contato_nome: l.contato_nome || "",
    // Apelido: contato_sobrenome guarda "como prefere ser chamado", não o
    // sobrenome (CRM-21) — por isso a coluna é separada e nunca concatenada.
    apelido: l.contato_sobrenome || "",
    contato_telefone: l.contato_telefone || "",
    contato_email: l.contato_email || "",
    telefone_secundario: l.telefone_secundario || "",
    email_secundario: l.email_secundario || "",
    empresa: l.empresa || "",
    cargo: l.cargo || "",
    instagram: l.instagram || "",
    site: l.site || "",
    data_nascimento: l.data_nascimento || "",
    descricao_bio: l.descricao_bio || "",
    cidade: l.cidade || "",
    estado: l.estado || "",
    cep: l.cep || "",
    endereco_principal: l.endereco_principal || "",
    cliente: l.cliente_nome || "",
    // Coluna enxuta do PDF: cliente vinculado OU o nome do contato.
    cliente_ou_contato: l.cliente_nome || l.contato_nome || "",
    responsavel: l.responsavel_nome || "",
    origem: l.origem_display || l.origem || "",
    origem_outro: l.origem_outro || "",
    valor_estimado: Number(l.valor_estimado) || 0,
    tags: Array.isArray(l.tags) ? l.tags.join(", ") : l.tags || "",
    parceiros:
      l.parceiros_nomes
      || parceiros.map((p) => p.representante_nome).filter(Boolean).join(", "),
    observacoes: l.observacoes || "",
    proxima_acao: l.proxima_acao || "",
    data_proxima_acao: l.data_proxima_acao || "",
    ganho_em: l.ganho_em || "",
    perdido_em: l.perdido_em || "",
    motivo_perda: l.motivo_perda_display || l.motivo_perda || "",
    entrou_etapa_em: l.entrou_etapa_em || "",
    criado: l.created_at || "",
    arquivado: l.arquivado ? "Sim" : "Não",
  };
}

// Colunas COMPLETAS — todos os campos do lead. Ordem lógica: identificação ->
// pipeline -> contato -> empresa/endereço -> negócio -> follow-up -> terminais.
// xlsx e csv usam o conjunto completo.
export const COLUNAS_COMPLETAS = [
  { key: "numero", label: "Número", type: "text" },
  { key: "titulo", label: "Título", type: "text" },
  { key: "etapa", label: "Etapa", type: "text" },
  { key: "grupo", label: "Grupo", type: "text" },
  { key: "status", label: "Status", type: "text" },
  { key: "temperatura", label: "Temperatura", type: "text" },
  { key: "contato_nome", label: "Contato (nome)", type: "text" },
  { key: "apelido", label: "Apelido", type: "text" },
  { key: "contato_telefone", label: "Telefone", type: "text" },
  { key: "contato_email", label: "E-mail", type: "text" },
  { key: "telefone_secundario", label: "Telefone 2", type: "text" },
  { key: "email_secundario", label: "E-mail 2", type: "text" },
  { key: "empresa", label: "Empresa", type: "text" },
  { key: "cargo", label: "Cargo", type: "text" },
  { key: "instagram", label: "Instagram", type: "text" },
  { key: "site", label: "Site", type: "text" },
  { key: "data_nascimento", label: "Nascimento", type: "date" },
  { key: "cidade", label: "Cidade", type: "text" },
  { key: "estado", label: "UF", type: "text" },
  { key: "cep", label: "CEP", type: "text" },
  { key: "endereco_principal", label: "Endereço", type: "text" },
  { key: "cliente", label: "Cliente", type: "text" },
  { key: "responsavel", label: "Responsável", type: "text" },
  { key: "origem", label: "Origem", type: "text" },
  { key: "origem_outro", label: "Origem (outro)", type: "text" },
  { key: "parceiros", label: "Parceiros", type: "text" },
  { key: "valor_estimado", label: "Valor estimado", type: "currency" },
  { key: "tags", label: "Tags", type: "text" },
  { key: "descricao_bio", label: "Bio", type: "text" },
  { key: "observacoes", label: "Observações", type: "text" },
  { key: "proxima_acao", label: "Próxima ação", type: "text" },
  { key: "data_proxima_acao", label: "Data prox. ação", type: "date" },
  { key: "ganho_em", label: "Ganho em", type: "date" },
  { key: "perdido_em", label: "Perdido em", type: "date" },
  { key: "motivo_perda", label: "Motivo da perda", type: "text" },
  { key: "entrou_etapa_em", label: "Entrou na etapa em", type: "date" },
  { key: "criado", label: "Criado em", type: "date" },
  { key: "arquivado", label: "Arquivado", type: "text" },
];

// PDF mantém um subconjunto enxuto (evita estourar a largura da página).
export const COLUNAS_PDF = [
  { key: "numero", label: "Número", type: "text" },
  { key: "titulo", label: "Título", type: "text" },
  { key: "etapa", label: "Etapa", type: "text" },
  { key: "cliente_ou_contato", label: "Cliente / contato", type: "text" },
  { key: "responsavel", label: "Responsável", type: "text" },
  { key: "origem", label: "Origem", type: "text" },
  { key: "valor_estimado", label: "Valor", type: "currency" },
  { key: "proxima_acao", label: "Próxima ação", type: "text" },
  { key: "data_proxima_acao", label: "Data prox. ação", type: "date" },
  { key: "criado", label: "Criado", type: "date" },
];

/** Cards de resumo do cabeçalho da exportação. */
export function resumoDaExportacao(linhas, metricas = {}) {
  return [
    { label: "Leads no pipeline", value: metricas.total ?? linhas.length, type: "number" },
    { label: "Valor total", value: Number(metricas.soma) || 0, type: "currency" },
    { label: "Etapas", value: metricas.etapas ?? 0, type: "number" },
  ];
}
